import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import Cookies from "js-cookie";
import { fetchPlanState, fetchUsage } from "../lib/billingApi";

// BYOK nudge: shown when a user CAN use BYOK but hasn't connected a key yet and is running a
// meaningful volume of AI generations. Two paths so it reaches both audiences:
//   • finite cap (LTD Tier 3/4): fire at ≥60% of the period allowance.
//   • unlimited cap (SaaS Pro/Business/Agency): pct is always 0, so fire on absolute volume
//     once they've run enough generations that moving to their own key is worthwhile.
// Dismiss hides it for the current billing period; it re-appears next period if the condition
// still holds (the dismiss key is scoped to the period boundary).
const KEY_PREFIX = "cfai_byok_nudge_dismissed:";
const WARN_PCT = 0.6;       // finite caps: ≥60% of the period allowance
const HEAVY_USE_ABS = 500;  // unlimited caps: generations this period before we nudge

function periodKeyFor(planState) {
    // Paid subs: scope to the real period end. Otherwise (free/LTD, no BillingSubscription):
    // fall back to a calendar-month bucket, matching the monthly AI-cap reset.
    return planState?.current_period_end
        ? new Date(planState.current_period_end).toISOString().slice(0, 10)
        : new Date().toISOString().slice(0, 7);
}

export default function ByokNudgeBanner() {
    const [show, setShow] = useState(false);
    const [periodKey, setPeriodKey] = useState("");

    const load = async () => {
        if (!Cookies.get("token")) return;
        try {
            const [ps, us] = await Promise.all([fetchPlanState(), fetchUsage()]);
            const meter = us?.meters?.ai_generations;
            const isUnlimited = !!meter?.unlimited;
            const heavyUse = isUnlimited
                ? (meter?.used || 0) >= HEAVY_USE_ABS   // no cap → judge by absolute volume
                : (meter?.pct || 0) >= WARN_PCT;         // finite cap → judge by % of allowance
            const eligible = ps?.byok_available === true && ps?.byok_active === false && heavyUse;
            const pk = periodKeyFor(ps);
            setPeriodKey(pk);
            const dismissed = localStorage.getItem(KEY_PREFIX + pk) === "1";
            setShow(eligible && !dismissed);
        } catch (_) { /* non-blocking */ }
    };

    useEffect(() => {
        load();
        const onChange = () => load();
        // plan-changed: disappears the moment BYOK is connected / plan upgraded.
        // usage-updated: appears mid-session the moment an AI generation crosses 60%.
        window.addEventListener("cfai:plan-changed", onChange);
        window.addEventListener("cfai:usage-updated", onChange);
        return () => {
            window.removeEventListener("cfai:plan-changed", onChange);
            window.removeEventListener("cfai:usage-updated", onChange);
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    if (!show) return null;

    const dismiss = () => {
        if (periodKey) localStorage.setItem(KEY_PREFIX + periodKey, "1");
        setShow(false);
    };

    return (
        <div className="border-b border-indigo-200 dark:border-indigo-700/60 bg-indigo-50 dark:bg-indigo-900/20 text-indigo-800 dark:text-indigo-200">
            <div className="max-w-6xl mx-auto px-4 py-2.5 flex flex-wrap items-center justify-between gap-2">
                <p className="text-sm font-medium">
                    💡 Save your AI cost cap forever — connect your OpenAI key for unlimited AI generations. Takes 30 seconds.
                </p>
                <div className="flex items-center gap-2">
                    <Link
                        to="/settings/billing?scroll=byok"
                        className="text-xs font-semibold px-3 py-1.5 rounded-lg bg-indigo-600 hover:bg-indigo-700 text-white no-underline"
                    >
                        Connect key
                    </Link>
                    <button onClick={dismiss} aria-label="Dismiss" className="text-current/60 hover:text-current px-1 text-lg leading-none">×</button>
                </div>
            </div>
        </div>
    );
}
